package editor.capture

import editor.EditorManager
import editor.SelectionManager
import editor.ui.DropdownItem
import editor.ui.DropdownManager.dropdown
import ext.threeColor
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.asCoroutineDispatcher
import kotlinx.coroutines.launch
import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLElement
import rendering.RendererManager
import util.PdfManager
import util.RenderHelper

object CaptureManager {
    private val scope = CoroutineScope(window.asCoroutineDispatcher())

    private var currentMode: CaptureMode = document.cookie.split(";")
        .firstOrNull { it.trim().startsWith("currentMode=") }
        ?.split("=")?.get(1)?.let(CaptureMode::valueOf) ?: CaptureMode.Outlines
        set(value) {
            field = value
            document.cookie = "currentMode=$value"
        }

    private var currentBackground: CaptureBackground = document.cookie.split(";")
        .firstOrNull { it.trim().startsWith("currentBackground=") }
        ?.split("=")?.get(1)?.let(CaptureBackground::valueOf) ?: CaptureBackground.Blue
        set(value) {
            field = value
            document.cookie = "currentBackground=$value"
            updateColor()
        }

    private fun updateColor() {
        val hexString = "#${currentBackground.color.threeColor.getHexString()}"
        val accentHexString = "#${currentBackground.accentColor.threeColor.getHexString()}"
        (document.getElementById("capture-indicator") as? HTMLElement)?.style?.borderColor = hexString
        (document.getElementById("capture-controls") as? HTMLElement)?.style?.apply {
            backgroundColor = hexString
            borderColor = hexString
        }
        (document.getElementById("capture-button") as? HTMLElement)?.style?.backgroundColor = accentHexString
    }

    /**
     * Tells the renderer to capture the next frame.
     */
    private var wantsToCapture = false

    /**
     * The data urls of the captured images for the current export operation.
     *
     * Cleared after the export operation is complete.
     */
    private val images = mutableListOf<String>()

    /**
     * Returns the text for the capture button on the right of the capture controls.
     */
    private fun captureBtnText(index: Int) = "Bild machen ($index/2)"

    /**
     * Called when the user clicks the button to export the scene.
     */
    fun startExport() {
        document.getElementById("capture-controls")?.classList?.toggle("hidden", false)
        document.getElementById("capture-indicator")?.classList?.toggle("hidden", false)
    }

    private fun finishExport() {
        document.getElementById("capture-controls")?.classList?.toggle("hidden", true)
        document.getElementById("capture-indicator")?.classList?.toggle("hidden", true)
        document.querySelector("#capture-button p")?.textContent = captureBtnText(1)

        scope.launch {
            PdfManager.createPdf(
                images = images.toList(),
                background = currentBackground,
                secondPageBackground = currentMode == CaptureMode.Outlines
            )
            images.clear()
        }
    }

    /**
     * Capture controls box:
     *   - [Bild (X/2)] button
     *   - mode dropdown
     * ---
     * - capture indicator (white outline)
     */
    fun addHtmlTo(parent: TagConsumer<HTMLElement>) {
        parent.div {
            id = "capture-controls"
            classes = setOf("hidden")

            parent.dropdown("Hintergrund", content = CaptureBackground.entries.map {
                DropdownItem(it.friendlyName) {
                    currentBackground = it
                }
            }, additionalClasses = setOf("background-dropdown"))

            parent.dropdown("Modus", content = CaptureMode.entries.map {
                DropdownItem(it.friendlyName) { currentMode = it }
            }, additionalClasses = setOf("capture-mode-dropdown"))

            parent.div {
                id = "capture-button"
                p { +captureBtnText(1) }
                onClickFunction = {
                    wantsToCapture = true
                    document.querySelector("#capture-button p")?.textContent = captureBtnText(2)
                }
            }
        }

        parent.div {
            id = "capture-indicator"
            classes = setOf("hidden")
        }

        updateColor()
    }

    /**
     * Called *every* time (disregarding [wantsToCapture]) by the [RendererManager] before rendering the scene.
     */
    fun beforeRender() {
        if (!wantsToCapture) return

        EditorManager.axesHelper.visible = false
        if (currentMode == CaptureMode.Outlines) SelectionManager.selectOutlinesForRender()
    }

    /**
     * Called *every* time (disregarding [wantsToCapture]) by the [RendererManager] after rendering the scene.
     */
    fun afterRender(canvas: HTMLCanvasElement) {
        if (!wantsToCapture) return

        wantsToCapture = false
        EditorManager.axesHelper.visible = true

        val defaultData = canvas.toDataURL("img/png")

        when (currentMode) {
            CaptureMode.Outlines -> RenderHelper.outlineRender(defaultData) {
                images.add(it)
                // async, so we have to check again
                if (images.size >= 2) finishExport()
            }
            CaptureMode.Realistic -> images.add(defaultData)
        }

        if (images.size >= 2) finishExport()
    }
}